/* eslint-disable @typescript-eslint/no-empty-function */
import { Roles } from '@cambridgeassessment/ciam-commons';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { INTERNAL_DOMAINS, SCHOOL_SUPPORT_HUB_ROLES } from 'src/constants';
import useFeatureFlags from 'src/hooks/useFeatureFlags';
import { getUserDetails } from 'src/services/apiService';
import {
  clearCookies,
  getAccountInfo,
  logoutRedirect,
  showLoginScreen,
  waitForGigya,
} from 'src/services/gigya/gigya-service';
import { decodeHashToParams } from 'src/utils/decodeHashToParams';
import { toDateString } from 'src/utils/transformers';
import envVariables from '../env';
import { AccountInfo, AuthStatus, Identities, User } from '../types';

interface CurrentOrganisation {
  bpid: string;
  name: string;
  legacyNumber?: string;
  centerType?: string;
}
interface IUserContext {
  user: User | null;
  status: AuthStatus;
  login: () => void;
  logout: () => void;
  update: () => void;
  isDelegate: boolean;
  isSSHUser: boolean;
  hasSSHInParentOrg: boolean;
  hasDelegateInParentOrg: boolean;
  isInternalUser: boolean;
  getCurrentOrg: () => CurrentOrganisation;
  isPDAUser?: boolean;
  areAllOrgsStandard?: boolean;
}

const UserContext = createContext<IUserContext>({
  user: null,
  status: AuthStatus.Loading,
  login: () => {
    throw new Error('<UserProvider> missing in a component tree');
  },
  logout: () => {
    throw new Error('<UserProvider> missing in a component tree');
  },
  update: () => {
    throw new Error('<UserProvider> missing in a component tree');
  },
  isDelegate: false,
  isPDAUser: false,
  isInternalUser: false,
  isSSHUser: false,
  hasSSHInParentOrg: false,
  hasDelegateInParentOrg: false,
  getCurrentOrg: () => ({
    bpid: null,
    name: '',
    legacyNumber: '',
  }),
  areAllOrgsStandard: true,
});

const useAuth = (): IUserContext => useContext(UserContext);

const mapSocialProviders = (socialProviders: string): string[] => {
  return socialProviders.split(',').map((provider) => {
    const formatedProvider = provider.toLowerCase().trim();
    if (formatedProvider === 'googleplus') {
      return 'google';
    }
    if (formatedProvider === 'messenger') {
      return 'microsoft';
    }
    return formatedProvider;
  });
};

const mapIdentityProviders = (identities: Identities[]): Identities[] => {
  return identities.map((identity) => {
    const formatedProvider = identity.provider.toLowerCase().trim();
    if (formatedProvider === 'googleplus') {
      identity.provider = 'google';
    }
    if (formatedProvider === 'messenger') {
      identity.provider = 'microsoft';
    }
    return identity;
  });
};

const mapToInternalUser = (
  update,
  CAS_BPID,
  _isEnabled: (flagName: string) => boolean
) => {
  const { firstName, lastName, email, photoURL, country } = update.profile;

  const verifiedEmails = update.loginIDs?.emails
    ?.map((e) => e.toLowerCase())
    .filter((e) => e !== email?.toLowerCase());

  const unverifiedEmails = update.loginIDs?.unverifiedEmails.map((e) =>
    e.toLowerCase()
  );

  const hasPassword = update.hasPassword;
  const subscriptions = update.subscriptions;
  const { lastSelectedOrg = null, claims = {} } = update.data || {};
  const { orgs = [], roles = [] } = claims;

  const lastLogin = update.lastLoginTimestamp
    ? toDateString(update.lastLoginTimestamp)
    : '';

  const organisations = orgs.filter((it) => it.bpid && it.name);

  const findLastSelectedOrg = () => {
    return (
      organisations.find((org) => org.bpid === lastSelectedOrg) ||
      organisations
        .flatMap((org) => org.childIds || [])
        .find((childOrg) => childOrg.bpid === lastSelectedOrg)
    );
  };

  const currentOrganisation = lastSelectedOrg ? findLastSelectedOrg() : null;

  const organisationLegacyNumber = currentOrganisation?.sourceSystems?.find(
    (source) => source.systemId === 'LegacyCustomerNum'
  )?.orgId;

  const socialProviders = mapSocialProviders(update.socialProviders);

  let formattedRoles = [];
  let isCambridgeAssessor = false;
  if (currentOrganisation !== null) {
    const orgRoles = roles.filter(
      (role) => role.bpid === currentOrganisation.bpid
    );
    formattedRoles = orgRoles[0]?.roleNames
      .map((name) => {
        return name.replace(new RegExp(/_/g), ' ');
      })
      .sort();
    isCambridgeAssessor = currentOrganisation.bpid === CAS_BPID;
  }

  const identities = mapIdentityProviders(update.identities);

  return {
    id: update.UID,
    fullName: `${firstName} ${lastName || ''}`.trim(),
    firstName: firstName,
    lastName: lastName,
    country: country,
    email: email,
    verifiedEmails: verifiedEmails,
    unverifiedEmails: unverifiedEmails,
    profilePicture: photoURL,
    hasPassword: hasPassword,
    organisationId: (currentOrganisation || {}).bpid,
    organisations: organisations,
    organisationName: (currentOrganisation || {}).name || '',
    organisationLegacyNumber,
    isCambridgeAssessor,
    subscriptions,
    lastLogin,
    socialProviders: socialProviders || [],
    roles: formattedRoles,
    identities: identities,
  };
};

export const UserProvider: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<AuthStatus>(AuthStatus.Loading);
  const [lastUpdated, setLastUpdated] = useState(Date.now());
  const [isDelegate, setIsDelegate] = useState(false);
  const [isInternalUser, setIsInternalUser] = useState(false);
  const [isSSHUser, setIsSSHUser] = useState(false);
  const [hasSSHInParentOrg, setHasSSHInParentOrg] = useState(false);
  const [hasDelegateInParentOrg, setHasDelegateInParentOrg] = useState(false);
  const [areAllOrgsStandard, setAreAllOrgsStandard] = useState(false);

  const location = useLocation();
  const history = useHistory();
  const { isEnabled, haveFlagsLoaded, updateContext } = useFeatureFlags();

  const isPDAUser = useMemo(() => {
    return !!user?.roles.includes(
      Roles.PROFESSIONAL_DEVELOPMENT_APPROVER.split('_').join(' ')
    );
  }, [user]);

  const getCurrentOrg = (): CurrentOrganisation | null => {
    if (!user?.organisationId || !user?.organisations) return null;
    const bpid = user.organisationId;
    let organisation = user.organisations.find((org) => org.bpid === bpid);
    if (!organisation) {
      for (const org of user.organisations) {
        const childOrg = org.childIds?.find((child) => child.bpid === bpid);
        if (childOrg) {
          organisation = childOrg;
          break;
        }
      }
    }
    if (!organisation) return null;

    const legacyNumber = organisation.sourceSystems?.find(
      (source) => source.systemId === 'LegacyCustomerNum'
    )?.orgId;
    const centerType = organisation.centerType;

    return {
      bpid,
      name: organisation.name,
      legacyNumber,
      centerType: organisation.centerType,
    };
  };

  // Helper function to find parent org and check for specific roles
  const checkParentOrgRoles = useCallback(
    (accountInfo: AccountInfo, roleToCheck: string | string[]): boolean => {
      const lastSelectedOrg = accountInfo.data?.lastSelectedOrg;
      if (!accountInfo.data?.claims?.orgs || !lastSelectedOrg) return false;

      // Find if current org is a child org
      const organisations = accountInfo.data.claims.orgs.filter(
        (it) => it.bpid && it.name
      );

      let parentOrg = null;
      for (const org of organisations) {
        const isParentOfCurrentOrg = org.childIds?.some(
          (child) => child.bpid === lastSelectedOrg
        );
        if (isParentOfCurrentOrg) {
          parentOrg = org;
          break;
        }
      }

      // If it's a child org, check if user has specific roles in parent org
      if (parentOrg) {
        const parentOrgRoles = accountInfo.data.claims.roles?.find(
          (el) => el.bpid === parentOrg.bpid
        )?.roleNames;

        if (Array.isArray(roleToCheck)) {
          return (
            parentOrgRoles?.some((role) => roleToCheck.includes(role)) || false
          );
        } else {
          return parentOrgRoles?.includes(roleToCheck) || false;
        }
      }

      return false;
    },
    []
  );

  const isDelegateCallback = useCallback(
    (accountInfo: AccountInfo) => {
      const lastSelectedOrg = accountInfo.data?.lastSelectedOrg;
      // Check if user has delegate role in current org
      const isDA =
        lastSelectedOrg &&
        accountInfo.data?.claims?.roles
          ?.find((el) => el.bpid === lastSelectedOrg)
          ?.roleNames?.includes(Roles.DELEGATED_ADMIN);
      setIsDelegate(isDA || false);

      // Check for delegated admin role in parent org
      const hasDelegateInParent = checkParentOrgRoles(
        accountInfo,
        Roles.DELEGATED_ADMIN
      );
      setHasDelegateInParentOrg(hasDelegateInParent);
    },
    [checkParentOrgRoles]
  );

  const isSSHUserCallback = useCallback(
    (accountInfo: AccountInfo) => {
      const lastSelectedOrg = accountInfo.data?.lastSelectedOrg;
      // Check if user has any SSH roles in current org
      const roles = accountInfo.data?.claims?.roles?.find(
        (el) => el.bpid === lastSelectedOrg
      )?.roleNames;
      const isSSH = roles?.some((role) =>
        SCHOOL_SUPPORT_HUB_ROLES.includes(role)
      );
      // Set state for current org SSH roles
      setIsSSHUser(isSSH || false);

      // Check for SSH roles in parent org
      const parentHasSSH = checkParentOrgRoles(
        accountInfo,
        SCHOOL_SUPPORT_HUB_ROLES
      );
      setHasSSHInParentOrg(parentHasSSH);
    },
    [checkParentOrgRoles]
  );

  const isInternalUserCallback = useCallback((accountInfo: AccountInfo) => {
    const email = accountInfo.profile?.email;
    if (!email) return;

    const emailDomain = email.split('@')[1]?.toLowerCase();
    const isInternal = INTERNAL_DOMAINS.some(
      (domain) => emailDomain === domain
    );
    setIsInternalUser(isInternal);
  }, []);

  const initiateLogin = useCallback(() => {
    (async () => {
      await waitForGigya();
      showLoginScreen(handleUserUpdate);
    })();
  }, []);

  const initiateLogout = useCallback(() => {
    setUser(null);
    clearCookies();
    logoutRedirect();
  }, []);

  const initiateUpdate = useCallback(() => {
    setLastUpdated(Date.now());
  }, [setLastUpdated]);

  const handleUserUpdate = useCallback(
    async (update) => {
      if (!update) return;
      const updateStatus = update.status || (update.response || {}).status;
      if (updateStatus === 'FAIL') {
        setStatus(AuthStatus.Unauthenticated);
        return;
      } else if (update.errorCode) {
        setStatus(AuthStatus.Error);
        return;
      }

      setStatus(AuthStatus.Authenticated);

      try {
        const result = await getUserDetails();
        const CAS_BPID = envVariables.CAS_BPID;
        if (result) {
          isDelegateCallback(result);
          isSSHUserCallback(result);
          isInternalUserCallback(result);
          setUser((prev) => ({
            ...(prev || {}),
            ...mapToInternalUser(result, CAS_BPID, isEnabled),
          }));
        }
        return;
      } catch (err) {
        return;
      }
    },
    [
      setUser,
      isEnabled,
      isDelegateCallback,
      isSSHUserCallback,
      isInternalUserCallback,
      haveFlagsLoaded,
    ]
  );

  useEffect(() => {
    (async () => {
      setStatus(AuthStatus.Loading);
      await waitForGigya();
      getAccountInfo(handleUserUpdate);
    })();
  }, [lastUpdated]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      initiateUpdate();
    }, 30000);

    // Clean up interval on unmount
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const params = decodeHashToParams(location.hash);

    if (location.hash) {
      history.replace(params.state);
    }
  }, []);

  useEffect(() => {
    if (user?.organisations) {
      const allOrgsStandard = user.organisations.every(
        (org) => org.centerType?.toLowerCase() === 'standard'
      );
      setAreAllOrgsStandard(allOrgsStandard);
    }
  }, [user?.organisations]);

  useEffect(() => {
    if (user?.id) {
      updateContext({
        userId: user.id,
        properties: { environmentName: envVariables.NODE_ENV },
      });
    }
  }, [user?.id, updateContext]);

  // Re-fetch user data when feature flags are loaded to apply filtering
  useEffect(() => {
    if (haveFlagsLoaded && user) {
      setLastUpdated(Date.now());
    }
  }, [haveFlagsLoaded]);

  return (
    <UserContext.Provider
      value={{
        user,
        isPDAUser,
        status,
        login: initiateLogin,
        logout: initiateLogout,
        update: initiateUpdate,
        isDelegate,
        isSSHUser,
        hasSSHInParentOrg,
        hasDelegateInParentOrg,
        isInternalUser,
        getCurrentOrg,
        areAllOrgsStandard,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export default useAuth;
